import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Divider,
  MenuItem,
  Select,
  Tabs,
  Tab
} from '@mui/material';
import { AddCircleOutline, Delete, Edit, Info, Search, Sort, TrendingDown, TrendingUp, FilterList } from '@mui/icons-material';
import Lottie from 'lottie-react';
import loadingAnimation from '../../../../assets/lottie/loading.json';
import api from '../../../../api/axios';
import { useCurrency } from '../../../../hooks/useCurrency';
import { moneyFromMinor } from '../../../utils/UnitConversion.utils';
import CreateBuyOrderModal from '../../../skin-db/offer-details/buy-order/CreateBuyOrderModal';
import ItemDetailLightModal from '../../../skin-db/offer-details/ItemDetailLightModal';
import { ItemCatalogDTO, ItemDTO } from '../../../user/on-sale/types';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import BuyOrderDeleteModal from './BuyOrderDeleteModal';
import BuyOrderInfoModal from './BuyOrderInfoModal';

export type BuyOrderStatus = 'PENDING' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';

export interface BuyOrder {
  id: number;
  itemName: string;
  imageLink: string;
  catalog: ItemCatalogDTO;
  quantity: number;
  completedQuantity: number;
  priceMinor: number;
  status: BuyOrderStatus;
  creationDate: string;
  steamAccountId: string;
}

const UserBuyOrder = () => {
  const { convertFromUSD, currencySymbol, formatAmountWithoutCurrency } = useCurrency();
  const steamAccounts = useSelector((state: RootState) => state.auth.steamAccounts);

  const [orders, setOrders] = useState<BuyOrder[]>([]);
  const [loading, setLoading] = useState(false);

  // Search & Filter States
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<BuyOrderStatus | 'ALL'>('ALL'); // Neuer Filter State
  const [inputValue, setInputValue] = useState('');
  const [searchOptions, setSearchOptions] = useState<string[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<BuyOrder | null>(null);
  const [lightModalItem, setLightModalItem] = useState<ItemDTO | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState('creationDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/profile/buy-orders`, {
        params: {
          sort: `${sortBy},${sortDirection}`,
          page,
          size: rowsPerPage,
          itemName: searchValue || undefined,
          status: statusFilter === 'ALL' ? undefined : statusFilter // Status an API senden
        }
      });
      setOrders(response.data.content);
      setTotalCount(response.data.totalElements);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [page, rowsPerPage, sortBy, sortDirection, searchValue, statusFilter]);

  const handleRequestSort = (property: string) => {
    const isAsc = sortBy === property && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const fetchSearchSuggestions = async (query: string) => {
    if (query.length < 2) {
      setSearchOptions([]);
      return;
    }
    setSearchLoading(true);
    try {
      const response = await api.get<string[]>(`/buy-orders/search-item`, { params: { itemName: query } });
      setSearchOptions(response.data);
    } catch (e) {
      console.error(e);
    } finally {
      setSearchLoading(false);
    }
  };

  // Helper für Status Farben in der Tabelle
  const getStatusStyle = (status: BuyOrderStatus) => {
    switch (status) {
      case 'PENDING': return 'bg-emerald-500/10 text-emerald-500';
      case 'PAUSED': return 'bg-yellow-500/10 text-yellow-500';
      case 'COMPLETED': return 'bg-blue-500/10 text-blue-500';
      case 'CANCELLED': return 'bg-red-500/10 text-red-500';
      default: return 'bg-neutral-500/10 text-neutral-500';
    }
  };

  const handleOpenLightModal = (order: BuyOrder) => {
    setLightModalItem({
      id: String(order.id),
      itemAssetId: '',
      catalog: order.catalog,
      details: null,
      attachments: [],
      lastSyncedAt: order.catalog.lastSyncedAt ?? order.creationDate,
    });
  };

  return (
      <div className="flex flex-col md:p-4 p-2 bg-white dark:bg-neutral-900/40 border border-gray-100 dark:border-white/5 min-h-[88vh]">
        {/* Header Section */}
        <div className="flex flex-col xl:flex-row justify-between items-center gap-4 mb-4 shrink-0">
          <h2 className="text-lg font-bold text-gray-800 dark:text-white uppercase tracking-tighter self-start xl:self-center">
            My Buy Orders
          </h2>

          {/* Container für Filter, Suche und Button */}
          <div className="flex flex-wrap flex-row w-full md:max-w-4xl gap-3 justify-between md:justify-end items-center">

            {/* Status Filter Tabs - Nimmt auf Mobile volle Breite ein (order-2), auf Desktop normal (order-1) */}
            <div className="flex bg-gray-50 dark:bg-neutral-800/50 p-1 rounded-none border border-black/5 dark:border-white/5 w-full md:w-auto order-2 md:order-1 justify-center md:justify-start">
              {['ALL', 'PENDING', 'PAUSED', 'COMPLETED', 'CANCELLED'].map((s) => (
                  <button
                      key={s}
                      onClick={() => {
                        setStatusFilter(s as any);
                        setPage(0);
                      }}
                      className={`flex-1 md:flex-none px-3 py-1.5 text-[10px] font-black uppercase tracking-tighter transition-all ${
                          statusFilter === s
                              ? 'bg-dark-primary text-white shadow-lg'
                              : 'text-neutral-500 hover:text-neutral-300'
                      }`}
                  >
                    {s}
                  </button>
              ))}
            </div>

            {/* Suche und Create Button Wrapper - Bleiben immer nebeneinander (order-1 auf mobile) */}
            <div className="flex flex-1 md:flex-none gap-2 items-center w-full md:w-auto order-1 md:order-2">
              <Autocomplete
                  fullWidth
                  size="small"
                  options={searchOptions}
                  loading={searchLoading}
                  value={searchValue}
                  inputValue={inputValue}
                  noOptionsText={inputValue.length < 2 ? "Search..." : "No items"}
                  onChange={(_, newValue) => {
                    setSearchValue(newValue);
                    setPage(0);
                  }}
                  onInputChange={(_, newInputValue) => {
                    setInputValue(newInputValue);
                    fetchSearchSuggestions(newInputValue);
                  }}
                  className="flex-1 md:w-64"
                  componentsProps={{
                    paper: {
                      sx: { borderRadius: 0, border: '1px solid rgba(255,255,255,0.05)', boxShadow: 'none' }
                    }
                  }}
                  renderInput={(params) => (
                      <TextField
                          {...params}
                          placeholder="Filter Name..."
                          variant="outlined"
                          className="bg-gray-50 dark:bg-neutral-800/50 md:!w-80 !w-52"
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.05)', borderRadius: 0 },
                            '& .MuiInputBase-input': { fontSize: '11px' }
                          }}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <InputAdornment position="start">
                                  <Search className="text-neutral-500" sx={{ fontSize: 18 }} />
                                </InputAdornment>
                            ),
                          }}
                      />
                  )}
              />

              <Button
                  variant="contained"
                  color="primary"
                  className="h-[36px] whitespace-nowrap flex items-center gap-2 text-xs font-bold px-4"
                  sx={{ borderRadius: 0, minWidth: { xs: '100px', md: '140px' } }}
                  startIcon={<AddCircleOutline sx={{ fontSize: 18 }} />}
                  onClick={() => {
                    setSelectedOrder(null);
                    setModalOpen(true);
                  }}
              >
                Create
              </Button>
            </div>
          </div>
        </div>

        <Divider className="!border-gray-100 dark:!border-white/5" />

        {loading && orders.length === 0 ? (
            <div className="flex-1 flex justify-center items-center">
              <Lottie animationData={loadingAnimation} loop className="w-64" />
            </div>
        ) : orders.length > 0 ? (
            <TableContainer component={Paper} className="bg-transparent shadow-none border-none mt-4 overflow-hidden" sx={{ borderRadius: 0 }}>
              <Table size="small" className="border-separate border-spacing-y-2">
                <TableHead>
                  <TableRow className="bg-gray-50/50 dark:bg-white/[0.02]">
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, border: 'none', borderRadius: 0 }} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Item</TableCell>
                    <TableCell sx={{ border: 'none', borderRadius: 0 }} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      <TableSortLabel
                          active={sortBy === 'itemName'}
                          direction={sortBy === 'itemName' ? sortDirection : 'asc'}
                          onClick={() => handleRequestSort('itemName')}
                          IconComponent={Sort}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, border: 'none', borderRadius: 0 }} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' }, border: 'none', borderRadius: 0 }} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Qty</TableCell>
                    <TableCell sx={{ border: 'none', borderRadius: 0 }} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      <TableSortLabel
                          active={sortBy === 'priceMinor'}
                          direction={sortBy === 'priceMinor' ? sortDirection : 'asc'}
                          onClick={() => handleRequestSort('priceMinor')}
                          IconComponent={Sort}
                      >
                        Price
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, border: 'none', borderRadius: 0 }} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Account</TableCell>
                    <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' }, border: 'none', borderRadius: 0 }} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Added At</TableCell>
                    <TableCell align="right" sx={{ border: 'none', borderRadius: 0 }} className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => {
                    const account = steamAccounts.find(a => a.id === order.steamAccountId);
                    return (
                        <TableRow
                            key={order.id}
                            className="group bg-gray-50/30 dark:bg-neutral-900/20 hover:bg-gray-100/50 dark:hover:bg-white/[0.03] transition-all duration-200"
                        >
                          <TableCell
                              onClick={() => handleOpenLightModal(order)}
                              sx={{ display: { xs: 'none', sm: 'table-cell' }, border: 'none', borderRadius: 0 }}
                              className="cursor-pointer"
                          >
                            <img src={order.imageLink} referrerPolicy="no-referrer" className="w-10 h-10 object-contain drop-shadow-md transition-transform duration-200 hover:scale-105" alt="" />
                          </TableCell>
                          <TableCell
                              onClick={() => handleOpenLightModal(order)}
                              sx={{ border: 'none', borderRadius: 0 }}
                              className="cursor-pointer group/name"
                          >
                            <div className="flex flex-col">
                              <span className="font-bold text-gray-700 dark:text-gray-200 group-hover/name:text-dark-primary transition-colors text-xs sm:text-sm">{order.itemName}</span>
                              <span className="text-[10px] text-gray-400 font-mono italic">#{order.id}</span>
                            </div>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, border: 'none', borderRadius: 0 }}>
                            <span className={`text-[10px] px-2 py-0.5 font-bold uppercase tracking-tighter border ${getStatusStyle(order.status)}`}>
                              {order.status}
                            </span>
                          </TableCell>
                          <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' }, border: 'none', borderRadius: 0 }}>
                            <span className="text-[10px] bg-white dark:bg-white/5 border border-gray-100 dark:border-white/5 px-2 py-1 font-mono font-bold">
                              {order.completedQuantity}/{order.quantity}
                            </span>
                          </TableCell>
                          <TableCell sx={{ border: 'none', borderRadius: 0 }}>
                            <span className="font-mono font-bold text-emerald-500 text-xs sm:text-sm">
                              {currencySymbol}{formatAmountWithoutCurrency(convertFromUSD(moneyFromMinor(order.priceMinor)))}
                            </span>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, border: 'none', borderRadius: 0 }}>
                            <div className="flex items-center gap-2">
                              <img src={account?.avatarUrl || ''} className="w-7 h-7 border border-gray-200 dark:border-white/10" alt="" />
                              <span className="text-[11px] text-gray-500 dark:text-gray-400 font-medium truncate max-w-[80px]">{account?.profileName || 'Unknown'}</span>
                            </div>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' }, border: 'none', borderRadius: 0 }} className="text-[10px] text-gray-400 font-medium">
                            {new Date(order.creationDate).toLocaleString('de-DE')}
                          </TableCell>
                          <TableCell align="right" sx={{ border: 'none', borderRadius: 0 }}>
                            <div className="flex justify-end gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                              <Tooltip title="Details" arrow>
                                <IconButton
                                    size="small"
                                    disableRipple
                                    className="!bg-transparent hover:!bg-dark-primary/20 transition-all"
                                    sx={{ borderRadius: 0 }}
                                    onClick={() => { setSelectedOrder(order); setInfoOpen(true); }}
                                >
                                  <div className="p-1.5 bg-dark-primary/10 flex items-center justify-center">
                                    <Info sx={{ fontSize: 16 }} className="text-dark-primary" />
                                  </div>
                                </IconButton>
                              </Tooltip>
                              {/* Edit Button - nur anzeigen wenn nicht Cancelled/Completed */}
                              {order.status !== 'CANCELLED' && order.status !== 'COMPLETED' && (
                                  <Tooltip title="Edit" arrow>
                                    <IconButton
                                        size="small"
                                        disableRipple
                                        className="!bg-transparent hover:!bg-yellow-500/10 transition-all"
                                        sx={{ borderRadius: 0 }}
                                        onClick={() => { setSelectedOrder(order); setModalOpen(true); }}
                                    >
                                      <div className="p-1.5 bg-yellow-500/10 flex items-center justify-center">
                                        <Edit sx={{ fontSize: 16 }} className="text-yellow-600" />
                                      </div>
                                    </IconButton>
                                  </Tooltip>
                              )}
                              {/* Cancel/Delete Button - nur anzeigen wenn noch aktiv */}
                              {order.status !== 'CANCELLED' && order.status !== 'COMPLETED' && (
                                  <Tooltip title="Cancel Order" arrow>
                                    <IconButton
                                        size="small"
                                        disableRipple
                                        className="!bg-transparent hover:!bg-red-500/10 transition-all"
                                        sx={{ borderRadius: 0 }}
                                        onClick={() => { setSelectedOrder(order); setDeleteOpen(true); }}
                                    >
                                      <div className="p-1.5 bg-red-500/10 flex items-center justify-center">
                                        <Delete sx={{ fontSize: 16 }} className="text-red-500" />
                                      </div>
                                    </IconButton>
                                  </Tooltip>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
        ) : (
            <div className="flex-1 flex flex-col items-center justify-center opacity-40">
              <FilterList sx={{ fontSize: 80 }} className="text-gray-300 mb-2" />
              <p className="text-sm font-bold text-gray-400 uppercase tracking-widest italic text-center">
                No {statusFilter !== 'ALL' ? statusFilter : ''} orders found
              </p>
            </div>
        )}

        {/* Footer / Pagination */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-100 dark:border-white/5 mt-auto shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-gray-400 font-bold uppercase">Rows</span>
            <Select
                value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                size="small"
                className="bg-gray-50 dark:bg-neutral-800/50 text-gray-600 dark:text-gray-300 !text-[10px] h-7"
                sx={{ '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.05)', borderRadius: 0 } }}
            >
              {[10, 20, 50].map(s => <MenuItem key={s} value={s} className="!text-xs">{s}</MenuItem>)}
            </Select>
          </div>
          <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[]}
              sx={{
                border: 'none',
                '& .MuiTablePagination-actions': {
                  '& .MuiButtonBase-root': {
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    color: '#3b82f6',
                    borderRadius: 0,
                    margin: '0 4px'
                  }
                }
              }}
          />
          <div className="text-[10px] text-gray-400 font-mono">{totalCount} total items</div>
        </div>

        {/* Modals */}
        <ItemDetailLightModal
            open={!!lightModalItem}
            onClose={() => setLightModalItem(null)}
            item={lightModalItem}
        />
        <BuyOrderInfoModal
            open={infoOpen}
            order={selectedOrder}
            onClose={() => setInfoOpen(false)}
            account={steamAccounts.find(a => a.id === selectedOrder?.steamAccountId)}
        />
        <BuyOrderDeleteModal
            open={deleteOpen}
            order={selectedOrder}
            onClose={() => setDeleteOpen(false)}
            onDeleted={fetchOrders}
        />
        <CreateBuyOrderModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            onSuccess={() => { fetchOrders(); setModalOpen(false); }}
            itemName={selectedOrder?.itemName || ''}
            initialData={selectedOrder ? {
              orderId: selectedOrder.id,
              priceMinor: selectedOrder.priceMinor,
              quantity: selectedOrder.quantity,
              steamAccount: steamAccounts.find(a => a.id === selectedOrder.steamAccountId) as any
            } : undefined}
        />
      </div>
  );
};

export default UserBuyOrder;
